import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ConstraintProfile } from 'types/ConstraintProfileTypes';
import constraintProfilesStore from 'shared/store/constraintProfilesStore';
import constraintsStore from 'shared/store/constraintsStore';
import { clearAgentChangeIndicators } from '../../utils/clearAgentChangeIndicators';
import {
  isThreadConstraintsActionable,
  useThreadConstraintsState,
} from '../../lifecycle/useThreadConstraintsState';

import styles from './savedProfiles.module.scss';

export const ConstraintProfileDropdown: React.FC = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const profilesByMode = constraintProfilesStore((s) => s.profilesByMode);
  const isLoading = constraintProfilesStore((s) => s.isLoading);
  const appliedProfile = constraintsStore((s) => s.appliedProfile);
  const isModified = constraintsStore((s) => s.isModified());
  const mode = constraintsStore((s) => s.mode);

  // Disable opening the dropdown (and therefore Apply / "Predefiniti" /
  // Manage) while the thread is still connecting or a silent sync is in
  // flight. Applying a profile during a sync would race the outgoing write
  // with a new one.
  const threadConstraintsState = useThreadConstraintsState();
  const lifecycleBlocked = !isThreadConstraintsActionable(threadConstraintsState);

  const modeProfiles = profilesByMode[mode] ?? [];

  // Trigger button label. `appliedProfile` always references either a live
  // profile or null — the deletion handler (ManageProfilesDialog) and the
  // restoration path (useConstraintsChangeDetection) both re-baseline any
  // dead reference to null (predefiniti) and silent-sync that into thread
  // metadata, so there is no "orphan" state to surface here.
  const baseLabel = appliedProfile
    ? appliedProfile.name
    : t('savedProfiles.defaults');

  // Loading state aside, the label is just the base name. The "modified"
  // state is surfaced by the trigger's border color (same orange used to
  // decorate modified input fields elsewhere in the Vincoli panel) so a
  // long truncated name never hides the indicator.
  const displayLabel = isLoading ? t('savedProfiles.loading') : baseLabel;
  // Full label including the textual "(modificato)" suffix — used as the
  // hover tooltip on the truncated label span for accessibility / screen
  // readers that don't pick up the border-color cue.
  const titleLabel =
    !isLoading && isModified
      ? `${baseLabel} (${t('savedProfiles.modified')})`
      : displayLabel;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={styles.dropdown} ref={dropdownRef}>
      {/*
        `showModifiedIndicator` gates the orange border on the trigger
        being actionable. During a profile switch (or any other write in
        flight) `lifecycleBlocked` flips true and the working document is
        briefly out of sync with the not-yet-restored applied profile —
        without this gate the trigger flashes orange for the ~1 second
        the seedThread is open. Suppressing the indicator while the
        trigger is disabled is also semantically correct: the user can't
        act on it, so the modified hint is moot until the operation
        settles.
      */}
      <button
        className={`${styles.dropdownTrigger} ${
          !isLoading && !lifecycleBlocked && isModified
            ? styles.dropdownTriggerModified
            : ''
        }`}
        onClick={() => !isLoading && !lifecycleBlocked && setOpen((prev) => !prev)}
        style={
          isLoading || lifecycleBlocked
            ? { opacity: 0.5, cursor: 'default' }
            : undefined
        }
        aria-label={
          !isLoading && !lifecycleBlocked && isModified
            ? `${baseLabel} (${t('savedProfiles.modified')})`
            : undefined
        }
      >
        <span className={styles.dropdownLabel} title={titleLabel}>{displayLabel}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className={open ? styles.chevronUp : styles.chevronDown}
        >
          <path d="M3 5l3 3 3-3" />
        </svg>
      </button>

      {open && (
        <div className={styles.dropdownMenu}>
          {/* Scrollable profile list (live catalog only) */}
          {modeProfiles.length > 0 && (
            <div className={styles.dropdownScrollArea}>
              {modeProfiles.map((profile: ConstraintProfile) => (
                <button
                  key={profile.id}
                  className={`${styles.dropdownItem} ${
                    profile.id === appliedProfile?.id
                      ? styles.dropdownItemActive
                      : ''
                  }`}
                  title={profile.name}
                  onClick={() => {
                    constraintsStore.getState().applyProfile(profile);
                    clearAgentChangeIndicators();
                    setOpen(false);
                  }}
                >
                  {profile.name}
                </button>
              ))}
            </div>
          )}

          {/* Fixed bottom items */}
          <div className={styles.dropdownFixedBottom}>
            <button
              className={`${styles.dropdownItem} ${
                !appliedProfile ? styles.dropdownItemActive : ''
              }`}
              onClick={() => {
                constraintsStore.getState().applyPredefiniti();
                clearAgentChangeIndicators();
                setOpen(false);
              }}
            >
              {t('savedProfiles.defaultsOption')}
            </button>

            <button
              className={styles.dropdownItemManage}
              onClick={() => {
                constraintProfilesStore.getState().setManageDialogOpen(true);
                setOpen(false);
              }}
            >
              {t('savedProfiles.manageProfiles')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
